import React, { useEffect, useState } from 'react';
import { Button, Card, Form } from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiPost } from '../../services/api';
import { resolveCompanyDestination } from '../../utils/companyRouting';
import { passwordMeetsStrength, passwordStrengthChecks } from '../../utils/passwordStrength';
import GoogleOAuthButton, { AuthDivider } from './GoogleOAuthButton';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default function Register() {
  const { user, token, authLoading, login, apiBase } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [role, setRole] = useState('candidate');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [emailFormatError, setEmailFormatError] = useState(false);
  const [passwordMismatchError, setPasswordMismatchError] = useState(false);
  const [passwordStrengthSubmitAttempt, setPasswordStrengthSubmitAttempt] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setRole(params.get('role') === 'company' ? 'company' : 'candidate');
  }, [location.search]);

  useEffect(() => {
    if (authLoading || !user) return;
    if (user.email_verified_at) {
      (async () => {
        const path = await resolveCompanyDestination(apiBase, token, user.role);
        navigate(path, { replace: true });
      })();
    }
  }, [user, authLoading, navigate, apiBase, token]);

  function validateEmailFormat(value) {
    const trimmed = value.trim();
    if (!trimmed) return true;
    return EMAIL_REGEX.test(trimmed);
  }

  function validatePasswordMismatch() {
    if (!passwordConfirmation) return true;
    return password === passwordConfirmation;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    setEmailFormatError(false);
    setPasswordMismatchError(false);
    setPasswordStrengthSubmitAttempt(false);
    setTermsError(false);

    if (!validateEmailFormat(email)) {
      setEmailFormatError(true);
      return;
    }
    if (!passwordMeetsStrength(password)) {
      setPasswordStrengthSubmitAttempt(true);
      return;
    }
    if (!validatePasswordMismatch()) {
      setPasswordMismatchError(true);
      return;
    }
    if (!termsAccepted) {
      setTermsError(true);
      return;
    }

    if (!apiBase) {
      setError('Registration is unavailable.');
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        role,
        first_name: firstName,
        last_name: lastName,
        email: email.trim(),
        password,
        password_confirmation: passwordConfirmation,
        terms_accepted: termsAccepted,
      };
      const res = await apiPost('/api/v1/auth/register', body);
      const data = await res.json().catch(() => ({}));
      if (res.status === 429) {
        setError('Too many attempts. Please wait a minute and try again.');
        return;
      }
      if (res.status === 422 && data.errors) {
        setFieldErrors(data.errors);
        return;
      }
      if (!res.ok) {
        setError(data.message || 'Something went wrong. Please try again.');
        return;
      }
      const u = data?.data?.user;
      const t = data?.data?.token;
      if (u && t) {
        login(u, t);
        navigate('/verify-email-pending', { replace: true });
      }
    } finally {
      setSubmitting(false);
    }
  }

  const checks = passwordStrengthChecks(password);
  const allStrengthMet = passwordMeetsStrength(password);
  const showPasswordRequirements =
    !allStrengthMet || passwordFocused || passwordStrengthSubmitAttempt;

  if (authLoading) {
    return (
      <div className="rwm-auth bg-white">
        <div className="rwm-auth__card py-5 text-center text-muted">
          <span>Loading…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="rwm-auth bg-white">
      <div className="rwm-auth__card py-5">
        <Card className="border" style={{ borderColor: 'var(--rwm-border)' }}>
          <Card.Body className="p-4">
            <h1 className="h4 fw-semibold mb-3" style={{ color: 'var(--rwm-dark)' }}>
              Create account
            </h1>
            <div className="rwm-auth__toggle d-flex mb-4" role="group" aria-label="Account type">
              <Button
                type="button"
                variant={role === 'candidate' ? 'info' : 'outline-secondary'}
                className={`flex-fill rounded-0 rounded-start ${role === 'candidate' ? 'text-white' : ''}`}
                onClick={() => setRole('candidate')}
                data-testid="rwm-toggle-candidate"
              >
                I&apos;m looking for a job
              </Button>
              <Button
                type="button"
                variant={role === 'company' ? 'info' : 'outline-secondary'}
                className={`flex-fill rounded-0 rounded-end ${role === 'company' ? 'text-white' : ''}`}
                onClick={() => setRole('company')}
                data-testid="rwm-toggle-company"
              >
                I&apos;m hiring
              </Button>
            </div>
            {role === 'candidate' ? (
              <>
                <GoogleOAuthButton
                  testId="rwm-register-google-btn"
                  apiBase={apiBase}
                  disabled={submitting}
                />
                <AuthDivider />
              </>
            ) : null}
            <Form data-testid="rwm-register-form" onSubmit={handleSubmit}>
              <Form.Group className="mb-3" controlId="registerFirstName">
                <Form.Label>First name</Form.Label>
                <Form.Control
                  value={firstName}
                  onChange={(ev) => setFirstName(ev.target.value)}
                  required
                  maxLength={100}
                />
                {fieldErrors.first_name ? (
                  <div className="rwm-auth__field-error mt-1">{fieldErrors.first_name[0]}</div>
                ) : null}
              </Form.Group>
              <Form.Group className="mb-3" controlId="registerLastName">
                <Form.Label>Last name</Form.Label>
                <Form.Control
                  value={lastName}
                  onChange={(ev) => setLastName(ev.target.value)}
                  required
                  maxLength={100}
                />
                {fieldErrors.last_name ? (
                  <div className="rwm-auth__field-error mt-1">{fieldErrors.last_name[0]}</div>
                ) : null}
              </Form.Group>
              <Form.Group className="mb-3" controlId="registerEmail">
                <Form.Label>{role === 'company' ? 'Work email' : 'Email'}</Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={(ev) => {
                    setEmail(ev.target.value);
                    setEmailFormatError(false);
                  }}
                  onBlur={() => {
                    if (!email.trim()) {
                      setEmailFormatError(false);
                      return;
                    }
                    setEmailFormatError(!validateEmailFormat(email));
                  }}
                  required
                  autoComplete="email"
                />
                {fieldErrors.email ? (
                  <div className="rwm-auth__field-error mt-1">{fieldErrors.email[0]}</div>
                ) : emailFormatError ? (
                  <div className="rwm-auth__field-error mt-1" data-testid="rwm-register-email-error">
                    Please enter a valid email address.
                  </div>
                ) : null}
              </Form.Group>
              <Form.Group className="mb-3" controlId="registerPassword">
                <Form.Label>Password</Form.Label>
                <div className="d-flex gap-2 align-items-center">
                  <Form.Control
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(ev) => {
                      const next = ev.target.value;
                      setPassword(next);
                      setPasswordStrengthSubmitAttempt(false);
                      if (passwordMismatchError && next === passwordConfirmation) {
                        setPasswordMismatchError(false);
                      }
                    }}
                    onFocus={() => setPasswordFocused(true)}
                    onBlur={() => setPasswordFocused(false)}
                    required
                    autoComplete="new-password"
                  />
                  <Button
                    type="button"
                    variant="outline-secondary"
                    size="sm"
                    className="flex-shrink-0"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </Button>
                </div>
                {fieldErrors.password ? (
                  <div className="rwm-auth__field-error mt-1">{fieldErrors.password[0]}</div>
                ) : null}
                {showPasswordRequirements ? (
                  <ul
                    className="list-unstyled small mb-0 mt-2"
                    data-testid="rwm-register-password-requirements"
                  >
                    <li style={{ color: checks.length ? 'var(--rwm-success)' : 'var(--rwm-slate)' }}>
                      {checks.length ? '✓' : '✗'} At least 8 characters
                    </li>
                    <li style={{ color: checks.upper ? 'var(--rwm-success)' : 'var(--rwm-slate)' }}>
                      {checks.upper ? '✓' : '✗'} One uppercase letter
                    </li>
                    <li style={{ color: checks.lower ? 'var(--rwm-success)' : 'var(--rwm-slate)' }}>
                      {checks.lower ? '✓' : '✗'} One lowercase letter
                    </li>
                    <li style={{ color: checks.number ? 'var(--rwm-success)' : 'var(--rwm-slate)' }}>
                      {checks.number ? '✓' : '✗'} One number
                    </li>
                    <li style={{ color: checks.special ? 'var(--rwm-success)' : 'var(--rwm-slate)' }}>
                      {checks.special ? '✓' : '✗'} One special character (for example !, @, #, $)
                    </li>
                  </ul>
                ) : null}
              </Form.Group>
              <Form.Group className="mb-3" controlId="registerPasswordConfirm">
                <Form.Label>Confirm password</Form.Label>
                <div className="d-flex gap-2 align-items-center">
                  <Form.Control
                    type={showPassword2 ? 'text' : 'password'}
                    value={passwordConfirmation}
                    onChange={(ev) => {
                      const v = ev.target.value;
                      setPasswordConfirmation(v);
                      if (passwordMismatchError && v === password) {
                        setPasswordMismatchError(false);
                      }
                    }}
                    onBlur={() => {
                      if (!passwordConfirmation) {
                        setPasswordMismatchError(false);
                        return;
                      }
                      setPasswordMismatchError(password !== passwordConfirmation);
                    }}
                    required
                    autoComplete="new-password"
                  />
                  <Button
                    type="button"
                    variant="outline-secondary"
                    size="sm"
                    className="flex-shrink-0"
                    onClick={() => setShowPassword2((v) => !v)}
                    aria-label={showPassword2 ? 'Hide confirm password' : 'Show confirm password'}
                  >
                    {showPassword2 ? 'Hide' : 'Show'}
                  </Button>
                </div>
                {fieldErrors.password_confirmation ? (
                  <div className="rwm-auth__field-error mt-1">{fieldErrors.password_confirmation[0]}</div>
                ) : passwordMismatchError ? (
                  <div className="rwm-auth__field-error mt-1" data-testid="rwm-register-password-mismatch-error">
                    Passwords do not match.
                  </div>
                ) : null}
              </Form.Group>
              <Form.Group className="mb-3" controlId="registerTerms">
                <Form.Check
                  type="checkbox"
                  id="registerTermsCheck"
                  checked={termsAccepted}
                  onChange={(e) => {
                    setTermsAccepted(e.target.checked);
                    setTermsError(false);
                  }}
                  data-testid="rwm-register-terms"
                  label={
                    <span>
                      I have read and agree to the{' '}
                      <Link to="/terms" target="_blank" rel="noopener noreferrer">
                        Terms of Service
                      </Link>
                    </span>
                  }
                />
                {termsError || fieldErrors.terms_accepted ? (
                  <div className="rwm-auth__field-error mt-1" data-testid="rwm-register-terms-error">
                    {fieldErrors.terms_accepted?.[0] || 'You must accept the Terms of Service.'}
                  </div>
                ) : null}
              </Form.Group>
              {error ? (
                <p className="rwm-auth__field-error mb-3" data-testid="rwm-register-error">
                  {error}
                </p>
              ) : null}
              <Button
                type="submit"
                variant="info"
                className="w-100 text-white btn-lg"
                disabled={submitting}
                data-testid="rwm-register-submit"
              >
                {submitting ? 'Creating account…' : 'Create account'}
              </Button>
            </Form>
            <p className="small text-center text-muted mt-3 mb-0">
              Already have an account?{' '}
              <Link to="/login" className="rwm-job-detail__browse-link text-decoration-none">
                Log In
              </Link>
            </p>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
}
